/**
 * The caller's SFDC accounts: the real call, and a mock standing in for it.
 *
 * ## One operation, two uses
 *
 * `GCRCompanyService` exposes a single `GetSfdcAccountInfo`, and which of its two optional
 * request fields you set decides what it does:
 *
 *     { ownerAlias }      list the accounts this person owns   -> the dropdown's options
 *     { sfdcAccountId }   look one up                          -> "is this id real"
 *
 * So verification is not a separate capability — it is the same lookup, and "found" means
 * "valid". Worth saying out loud, because a reader looking for a `ValidateX` operation will
 * not find one.
 *
 * ## Not the CPA backend
 *
 * This is a second service, reached with its own endpoint, so it is a second thing that can be
 * down while the questionnaire is otherwise fine. The UI has to say which one failed.
 *
 * ## A defect this wrapper does NOT reproduce
 *
 * The AgentHub version swallows every error and returns `null`:
 *
 *     catch (error) { console.error(...); return null; }
 *
 * so "this id does not exist" and "the service is unreachable" are the same answer, and a BD
 * facing an outage is told their correct account id is wrong — then checks it again. Here the
 * two are distinguished (`{ kind: 'invalid' }` vs `{ kind: 'error' }`), because that
 * difference is the whole message.
 */

// DISABLED with the file it points at — see the header of `RealSfdcAccountApi.ts`. The client
// package it needs is not in the version set yet, so importing it breaks the build.
// import { RealSfdcAccountApi } from './RealSfdcAccountApi';

export interface SfdcAccount {
  accountId: string;
  companyNameEn: string;
  companyNameZh?: string;
}

/** What a verification can conclude. Three outcomes, not two. */
export type VerifyResult =
  | { kind: 'valid'; account: SfdcAccount }
  | { kind: 'invalid' }
  | { kind: 'error'; message: string };

export interface SfdcAccountApi {
  /** `GetSfdcAccountInfo({ ownerAlias })` — the dropdown's options. */
  listOwnAccounts(ownerAlias: string): Promise<SfdcAccount[]>;
  /** `GetSfdcAccountInfo({ sfdcAccountId })` — found means valid. */
  verify(accountId: string): Promise<VerifyResult>;
}

/* ── Mock ───────────────────────────────────────────────────────────────────── */

/**
 * Accounts the dropdown branch can show — used ONLY when explicitly asked for.
 *
 * Not the default, and that is the point. The question's form is decided by this list's
 * length, in the live code as well as here:
 *
 *     questionType: sfdcAccounts.length > 0 ? "LIST_SELECTION" : "TEXT_INPUT"
 *
 * so a mock that always returns accounts makes the text branch unreachable and the app look
 * like it implements a different control than AgentHub does. It implements both; the data
 * picks. Real callers who are not SFDC account owners get zero rows and therefore the text
 * box, which is what `?sfdc=real` shows and what the default mock now matches.
 */
const MOCK_ACCOUNTS: SfdcAccount[] = [
  { accountId: '0015000000aB1cdEFG', companyNameEn: 'Shanghai Lingyun Tech', companyNameZh: '上海凌云科技' },
  { accountId: '0015000000bC2deFGH', companyNameEn: 'Beijing Haitian Data', companyNameZh: '北京海天数据' },
  { accountId: '0015000000cD3efGHI', companyNameEn: 'Shenzhen Qiyuan Cloud', companyNameZh: '深圳启元云' },
];

const MOCK_OTHER: SfdcAccount = {
  accountId: '0015000000oA6HvAAK',
  companyNameEn: 'Guangzhou Zhonghe Group',
  companyNameZh: '广州中和集团',
};

const LATENCY_MS = 260;
const sleep = (ms: number) => new Promise((r) => setTimeout(r, ms));

export class MockSfdcAccountApi implements SfdcAccountApi {
  /**
   * @param accounts what `listOwnAccounts` returns. EMPTY by default, because that is what
   * the service returns for a caller who owns no SFDC accounts — the common case, and the one
   * that produces the text box plus remote validation.
   */
  constructor(private readonly accounts: SfdcAccount[] = []) {}

  /**
   * `ownerAlias` is accepted and ignored — the mock has one caller. Kept in the signature
   * because the real call needs it, and a mock whose shape differs from the real one has to be
   * rewritten rather than replaced.
   */
  async listOwnAccounts(ownerAlias: string): Promise<SfdcAccount[]> {
    await sleep(LATENCY_MS);
    void ownerAlias;
    return this.accounts;
  }

  async verify(accountId: string): Promise<VerifyResult> {
    await sleep(LATENCY_MS);
    const id = accountId.trim();
    const found = [...MOCK_ACCOUNTS, MOCK_OTHER].find((a) => a.accountId === id);
    const result: VerifyResult = found
      ? { kind: 'valid', account: found }
      : // `__fail__` forces the third outcome, which is otherwise unreachable in a mock and is
        // the one whose handling is easiest to get wrong.
        id === '__fail__'
        ? { kind: 'error', message: 'GCRCompanyService 暂时不可用（mock）' }
        : { kind: 'invalid' };
    return result;
  }
}

/* ── Which one the app uses ─────────────────────────────────────────────────── */

let api: SfdcAccountApi | null = null;

/**
 * Which implementation runs, chosen by a URL flag.
 *
 *     (none)        mock, ZERO accounts  -> text box + validate on Next   <- matches AgentHub
 *     ?sfdc=list    mock, three accounts -> dropdown
 *     ?sfdc=real    GCRCompanyService
 *
 * The default is the empty mock on purpose. The form is data-decided, so a mock that hands
 * back accounts silently pins the app to one of the two branches — which is exactly how this
 * app came to look like it had replaced AgentHub's text box with a select. `?sfdc=list` keeps
 * the other branch reachable without making it the thing you see first.
 *
 * `real` is a flag rather than config because the question being tested is whether the call
 * works at all: it is cross-partition, and whether Rocky's cn pool is authorised to invoke
 * that service is not something this repo can answer by reading. It has to be tried, and
 * trying it must not be the default while the answer is unknown.
 *
 * Every path logs every call, so the console shows which one ran and what came back.
 */
export function getSfdcAccountApi(): SfdcAccountApi {
  if (!api) {
    const flag =
      typeof window === 'undefined' ? null : new URLSearchParams(window.location.search).get('sfdc');

    const banner = (bg: string, text: string) =>
      // eslint-disable-next-line no-console
      console.info(
        `%c[CPA SFDC]%c ${text}`,
        `background:${bg};color:#fff;padding:1px 6px;border-radius:3px;font-weight:600`,
        'color:#5f6b7a',
      );

    if (flag === 'real') {
      /*
       * `?sfdc=real` is accepted and then refused, loudly.
       *
       * Falling back to the mock silently would be worse than not having the flag: someone
       * testing the real service would read mock accounts and conclude it works. So the flag
       * still does something — it says why it cannot, and leaves the mock in place so the page
       * is usable.
       */
      api = new LoggingSfdcAccountApi(new MockSfdcAccountApi(), 'MOCK（real 已停用）');
      banner(
        '#8b1a10',
        '?sfdc=real 暂时停用 —— @amzn/gcr-company-service-client 还没进版本集（见 RealSfdcAccountApi.ts 文件头）。' +
          '现在走的仍然是 MOCK，下面看到的账户不是真数据。',
      );
    } else if (flag === 'list') {
      api = new LoggingSfdcAccountApi(new MockSfdcAccountApi(MOCK_ACCOUNTS), 'MOCK·list');
      banner('#8a6116', 'MOCK（3 个账户）—— 走下拉分支。去掉 ?sfdc=list 回到文本框分支（现网对多数 alias 的真实形态）');
    } else {
      api = new LoggingSfdcAccountApi(new MockSfdcAccountApi(), 'MOCK');
      banner(
        '#8a6116',
        'MOCK（0 个账户）—— 文本框 + Next 时校验，和 agenthub 一致。' +
          '输 0015000000oA6HvAAK 校验通过；输 __fail__ 触发服务异常分支；?sfdc=list 看下拉分支；?sfdc=real 试真服务',
      );
    }
  }
  return api;
}

/** For tests and for pinning one implementation. */
export function setSfdcAccountApi(next: SfdcAccountApi | null): void {
  api = next;
}

/**
 * Logs both implementations identically.
 *
 * Wrapping rather than logging inside each one: the whole point of trying the real service is
 * to compare its answers with the mock's, and two separate log formats make that comparison
 * by eye instead of by reading.
 */
class LoggingSfdcAccountApi implements SfdcAccountApi {
  constructor(
    private readonly inner: SfdcAccountApi,
    private readonly label: string,
  ) {}

  private badge(): [string, string, string] {
    return [
      `%c[CPA SFDC ${this.label}]%c`,
      'background:#5b2fb3;color:#fff;padding:1px 6px;border-radius:3px;font-weight:600',
      'color:#5f6b7a',
    ];
  }

  async listOwnAccounts(ownerAlias: string): Promise<SfdcAccount[]> {
    const t0 = performance.now();
    try {
      const r = await this.inner.listOwnAccounts(ownerAlias);
      const [f, a, b] = this.badge();
      // eslint-disable-next-line no-console
      console.info(
        `${f} GetSfdcAccountInfo({ ownerAlias: "${ownerAlias}" }) → ${r.length} 个账户 · ${Math.round(performance.now() - t0)}ms`,
        a,
        b,
      );
      // eslint-disable-next-line no-console
      if (r.length) console.table(r);
      return r;
    } catch (e) {
      const [f, a, b] = this.badge();
      // eslint-disable-next-line no-console
      console.error(`${f} GetSfdcAccountInfo({ ownerAlias }) 失败`, a, b, e);
      throw e;
    }
  }

  async verify(accountId: string): Promise<VerifyResult> {
    const t0 = performance.now();
    const r = await this.inner.verify(accountId);
    const [f, a, b] = this.badge();
    // eslint-disable-next-line no-console
    console.info(
      `${f} GetSfdcAccountInfo({ sfdcAccountId: "${accountId}" }) → ${r.kind} · ${Math.round(performance.now() - t0)}ms`,
      a,
      b,
      r,
    );
    return r;
  }
}
